import React from 'react';
import { searchSchools, SchoolHit } from '../api/schools';

/** Matchar MinQueryLength i SchoolSearchService — kortare söksträngar avvisas ändå. */
const MIN_QUERY_LENGTH = 2;

const DEBOUNCE_MS = 300;

interface SchoolSearchState {
    hits: SchoolHit[];
    loading: boolean;
    failed: boolean;
}

const IDLE: SchoolSearchState = { hits: [], loading: false, failed: false };

/**
 * Söker skolor medan läraren skriver.
 *
 * Anropet fördröjs så att en normal inskrivning ger ett anrop i stället för ett per
 * tangenttryckning, och varje ny sökning avbryter den föregående. Utan avbrottet kan
 * ett långsamt tidigare svar hinna landa efter ett nyare och skriva över träfflistan.
 */
export function useSchoolSearch(query: string, enabled: boolean): SchoolSearchState {
    const [state, setState] = React.useState<SchoolSearchState>(IDLE);

    React.useEffect(() => {
        const term = query.trim();

        if (!enabled || term.length < MIN_QUERY_LENGTH) {
            setState(IDLE);
            return;
        }

        const controller = new AbortController();
        setState((prev) => ({ ...prev, loading: true, failed: false }));

        const timer = window.setTimeout(() => {
            searchSchools(term, controller.signal)
                .then((hits) => setState({ hits, loading: false, failed: false }))
                .catch((error: unknown) => {
                    // Avbrutna anrop är förväntade och ska inte visas som fel.
                    if (error instanceof DOMException && error.name === 'AbortError') return;
                    setState({ hits: [], loading: false, failed: true });
                });
        }, DEBOUNCE_MS);

        return () => {
            window.clearTimeout(timer);
            controller.abort();
        };
    }, [query, enabled]);

    return state;
}
